import React, { useState, useEffect, useContext } from 'react';
import { client } from '../client';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { CursorContext } from '../context/CursorContext';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

const getExcerpt = (body, maxLength = 100) => {
  if (!body) return '';
  const text = body
    .filter((block) => block._type === 'block')
    .map((block) => block.children.map((child) => child.text).join(''))
    .join(' ');
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

export default function Blog() {
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);
  const theme = useTheme();
  const darkMode = theme?.darkMode ?? true;

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .fetch(`*[_type == "post"] {
        title,
        slug,
        body,
        mainImage {
          asset -> {
            _id,
            url 
          },
          alt,
        }, 
        "name": author -> name,
      } | order(publishedAt desc)`)
      .then((data) => {
        setPosts(data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <main
        style={{
          background: darkMode
            ? 'linear-gradient(to top right, #1e1b4b, #020617, #000000)'
            : '#ffffff',
        }}
        className="min-h-screen"
      />
    );
  }

  return (
    <>
      <main
        style={{
          background: darkMode
            ? 'linear-gradient(to top right, #1e1b4b, #020617, #000000)'
            : '#ffffff',
          color: darkMode ? '#ffffff' : '#0f172a',
          transition: 'all 0.3s ease',
        }}
        className="min-h-screen overflow-auto"
      >
        <section className="py-10">

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 1.2 }}
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: { opacity: 1, y: 0 },
            }}
            className="md:mx-auto"
          >

            <h1 className={`h1 sm:text-7xl items-center font-bold text-center lg:pb-9 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Blog
            </h1>

            <div className="grid grid-cols-1 gap-5 md:gap-4 md:grid-cols-2 lg:grid-cols-3 my-10 px-5">

              {posts.map((post) => (
                <article
                  className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
                    darkMode
                      ? 'border-white/10 bg-white/[0.03]'
                      : 'border-slate-200 bg-slate-50'
                  }`}
                  key={post.slug.current}
                >

                  {post.mainImage?.asset?.url && (
                    <img
                      src={post.mainImage.asset.url}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-auto object-cover"
                    />
                  )}

                  <div className="p-6 flex flex-col gap-3">

                    <h4 className={`text-xl font-bold font-primary text-center ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {post.title}
                    </h4>

                    <p className={`text-sm font-secondary text-center lg:px-10 leading-relaxed ${darkMode ? 'text-white/40' : 'text-slate-400'}`}>
                      {getExcerpt(post.body)}
                    </p>

                    <p className={`text-xs font-secondary text-center tracking-widest uppercase ${darkMode ? 'text-white/30' : 'text-slate-400'}`}>
                      By {post.name}
                    </p>

                    <div className="flex justify-center mt-2">
                      <Link
                        onMouseEnter={mouseEnterHandler}
                        onMouseLeave={mouseLeaveHandler}
                        to={`/blog/${post.slug.current}`}
                        className={`py-2 px-6 rounded border text-sm font-bold tracking-wide transition-all duration-500 ${
                          darkMode
                            ? 'text-white bg-black hover:bg-white border-slate-200 hover:text-black'
                            : 'text-slate-900 bg-white hover:bg-slate-900 border-slate-900 hover:text-white'
                        }`}
                      >
                        Read Full Article
                      </Link>
                    </div>

                  </div>

                </article>
              ))}

            </div>

          </motion.div>

        </section>

        <Footer />

      </main>
    </>
  );
}